<template>
  <div v-style:bg="containerBackgroundColor">
    <h2
      v-if="title"
      v-style:c="titleColor"
      class="pb-4 "
      :class="titleStyle"
      data-test-id="cms-icon-and-text-collection-title"
    >
      <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
      <span v-else>{{ title }}</span>
    </h2>
    <div
      v-if="backgroundImage"
      class="relative overflow-hidden"
    >
      <cms-base-responsive-media
        class="w-full"
        fit="cover"
        loop
        :media="{ sm: backgroundImage }"
      />
      <div class="absolute-0 grid cols-2 rows-3 md:cols-3" :class="[overlayContainerClass, { 'mb-6': isVideo }]">
        <div
          v-for="({
            backgroundColor,
            icon,
            positionResponsive,
            richText,
            spanResponsive,
            target,
            textColor,
            title: iconTitle,
            titleColor: iconTitleColor,
            titleStyle: iconTitleStyle = 'title-2',
            inCellPositionResponsive,
          }, key) in displayedItems"
          :key
          v-style="getOverlayItemStyle({ positionResponsive, spanResponsive })"
          class="relative f-col text-sm"
          :class="resolveInCellPositionClass(inCellPositionResponsive)"
        >
          <component
            :is="target ? BaseLink : 'div'"
            v-style="{
              bg: backgroundColor,
              c: textColor,
            }"
            :class="[layoutClass, overlaySpacingClass]"
            :target="target?.targetAttribute"
            :to="target?.url"
          >
            <vf-icon
              v-if="icon"
              :dir="icon.dir"
              :name="icon.icon"
              :size="resolveIconSize(iconTitle)"
              :src="icon.src"
            />
            <div class="space-y-2">
              <h3 v-if="iconTitle" v-style:c="iconTitleColor" :class="iconTitleStyle">
                {{ iconTitle }}
              </h3>
              <cms-rich-text v-if="richText" :content="{ richText }" />
            </div>
          </component>
        </div>
      </div>
      <slot />
    </div>
    <div
      v-else
      class="f-col"
      :class="{ 'md:flex-row md:justify-center md:wrap': variant !== 'one-column' }"
    >
      <component
        :is="target ? BaseLink : 'div'"
        v-for="({
          backgroundColor,
          icon,
          richText,
          target,
          textColor,
          title: iconTitle,
          titleColor: iconTitleColor,
          titleStyle: iconTitleStyle = 'title-2',
        }, key) in displayedItems"
        :key
        v-style="{
          bg: backgroundColor,
          w: { md: variant !== 'one-column' && `calc(100% / ${perRow})` },
          c: textColor,
        }"
        class="relative text-sm"
        :class="[layoutClass, spacingClass]"
        :target="target?.targetAttribute"
        :to="target?.url"
      >
        <vf-icon
          v-if="icon"
          :dir="icon.dir"
          :name="icon.icon"
          :size="resolveIconSize(iconTitle)"
          :src="icon.src"
        />
        <div class="space-y-2">
          <h3 v-if="iconTitle" v-style:c="iconTitleColor" :class="iconTitleStyle">
            {{ iconTitle }}
          </h3>
          <cms-rich-text v-if="richText" :content="{ richText }" />
        </div>
        <div :class="{ 'md:absolute md:inset-y-0 md:right-0 md:my-4 md:z-1 md:w-px md:bg-grey-60': !isLastInRow(key) }" />
        <div
          :class="{
            'absolute inset-x-0 bottom-0 mx-4 z-1 h-px bg-grey-60': key !== total - 1,
            'md:hidden': isInLastRow(key),
          }"
        />
      </component>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type {
  IconAndText,
  IconAndTextCollectionContent,
  IconAndTextSpan,
  IconInCellPosition
} from '#types/components/cms/icon-and-text-collection'
import type { IconSizes } from '#types/sizes'
import { BaseLink } from '#components'

const { content } = defineProps<{
  content: IconAndTextCollectionContent
}>()

const {
  backgroundColor: containerBackgroundColor,
  backgroundImage,
  iconAlignment,
  iconAndText,
  iconSize,
  tightPaddings,
  title,
  titleHtml,
  titleStyle,
  titleColor,
  variant
} = content

const { breakpoints } = useAppConfig().ds

const iconSizeMap = {
  small: 'md',
  medium: 'lg',
  standard: 'xl',
  large: '3xl'
} satisfies Record<NonNullable<typeof iconSize>, IconSizes>

const isIconAboveText = variant === 'icon-above-text' || (variant === 'icons-with-background' && iconAlignment === 'icon-above-text')
const layoutClass = isIconAboveText ? 'f-col items-center text-center' : 'flex'
const spacingClass = isIconAboveText
  ? (tightPaddings ? 'gap-y-1 p-2' : 'gap-y-4 p-10')
  : (tightPaddings ? 'gap-x-1 p-1' : 'gap-x-3 p-6')
const overlaySpacingClass = isIconAboveText
  ? (tightPaddings ? 'gap-y-1 p-2' : 'gap-y-3 p-4')
  : (tightPaddings ? 'gap-x-2 p-2' : 'gap-x-3 p-4')
const overlayContainerClass = tightPaddings ? 'gap-2 p-2 md:p-4' : 'gap-4 p-4 md:p-6'
const isVideo = backgroundImage?.type === 'CMVideo'

const iconInCellPositionSmall: Record<IconInCellPosition, string> = {
  'top-left': '<md:items-start <md:justify-start',
  'top-center': '<md:items-center <md:justify-start',
  'top-right': '<md:items-end <md:justify-start',
  'middle-left': '<md:items-start <md:justify-center',
  'middle-center': '<md:items-center <md:justify-center',
  'middle-right': '<md:items-end <md:justify-center',
  'bottom-left': '<md:items-start <md:justify-end',
  'bottom-center': '<md:items-center <md:justify-end',
  'bottom-right': '<md:items-end <md:justify-end',
}

const iconInCellPositionMedium: Record<IconInCellPosition, string> = {
  'top-left': 'md:items-start md:justify-start',
  'top-center': 'md:items-center md:justify-start',
  'top-right': 'md:items-end md:justify-start',
  'middle-left': 'md:items-start md:justify-center',
  'middle-center': 'md:items-center md:justify-center',
  'middle-right': 'md:items-end md:justify-center',
  'bottom-left': 'md:items-start md:justify-end',
  'bottom-center': 'md:items-center md:justify-end',
  'bottom-right': 'md:items-end md:justify-end',
}

const resolveInCellPositionClass = (inCellPosition?: Responsive<IconInCellPosition>) => {
  if (!inCellPosition) return []

  const position = normalizeResponsive(inCellPosition)

  return [
    iconInCellPositionSmall[position.sm],
    iconInCellPositionMedium[position.md]
  ]
}

const displayedItems = backgroundImage ? iconAndText.slice(0, 6) : iconAndText
const total = displayedItems.length
const perRow = Math.min(total === 4 ? 2 : 3, total)

type GridSpan = 1 | 2 | 3
type HorizontalPosition = 'left' | 'center' | 'right'
type VerticalPosition = 'top' | 'middle' | 'bottom'

const normalizeSpan = (span?: IconAndTextSpan): GridSpan => {
  const parsedSpan = Number(span || 1)

  return parsedSpan >= 1 && parsedSpan <= 3 ? parsedSpan as GridSpan : 1
}

const resolveColumnStart = (horizontal: HorizontalPosition, span: GridSpan, br: string) => {
  switch (horizontal) {
    case 'center':
      return Math.ceil((4 - span) / 2)
    case 'right':
      return (br === 'sm' ? 3 : 4) - span
    default:
      return 1
  }
}

const resolveRowStart = (vertical: VerticalPosition) => {
  switch (vertical) {
    case 'middle':
      return 2
    case 'bottom':
      return 3
    default:
      return 1
  }
}

const getOverlayItemStyle = ({
  positionResponsive,
  spanResponsive
}: Pick<IconAndText, 'positionResponsive' | 'spanResponsive'>) => {
  const position = normalizeResponsive(positionResponsive)
  const span = normalizeResponsive(spanResponsive)

  return Object.keys(breakpoints).reduce((acc, br) => {
    const gridSpan = normalizeSpan(span[br])
    const [vertical, horizontal] = position[br].split('-') as [VerticalPosition, HorizontalPosition]

    acc['grid-col'][br] = `${resolveColumnStart(horizontal, gridSpan, br)} / span ${gridSpan}`
    acc['grid-row'][br] = `${resolveRowStart(vertical)}`

    return acc
  }, { 'grid-col': {}, 'grid-row': {} } as Record<'grid-col' | 'grid-row', Partial<Responsive>>)
}

const isLastInRow = (index: number) => variant === 'one-column' || (index + 1) % perRow === 0 || index === total - 1
const isInLastRow = (index: number) => variant !== 'one-column' && index >= total - (((total % perRow) || perRow))
const resolveIconSize = (iconTitle?: string) => iconSizeMap[iconSize || (variant !== 'icon-above-text' && !iconTitle ? 'small' : 'standard')]
</script>
